import React, { useMemo, useRef, useState } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Reveal, SectionHeading } from "../shared/Primitives";
import { Search, Pin, Star, ArrowUpRight, Github, ExternalLink, X, Cpu, ListChecks, Route, Gauge } from "lucide-react";

const FILTERS = ["All", "AI", "Full Stack", "QA"];
const STATUS_COLORS = { Live: "text-green-400 bg-green-400/10", Testing: "text-amber-400 bg-amber-400/10", "In Progress": "text-blue-400 bg-blue-400/10" };

function TiltCard({ project, onOpen, i }) {
    const ref = useRef(null);
    const mx = useMotionValue(0), my = useMotionValue(0);
    const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 200, damping: 20 });
    const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 200, damping: 20 });
    const onMove = (e) => {
        const r = ref.current.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };
    const reset = () => { mx.set(0); my.set(0); };
    return (
        <Reveal delay={i * 0.05}>
            <motion.button ref={ref} onMouseMove={onMove} onMouseLeave={reset} onClick={() => onOpen(project)}
                style={{ rotateX: rx, rotateY: ry, transformPerspective: 1000 }}
                data-testid={`project-card-${i}`}
                className="group relative w-full text-left glass rounded-3xl overflow-hidden gradient-border">
                <div className="relative h-48 overflow-hidden" style={{ transform: "translateZ(40px)" }}>
                    <img src={project.image} alt={project.title} loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-card via-card/20 to-transparent" />
                    <div className="absolute top-3 left-3 flex gap-2">
                        {project.pinned && <span className="flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded-full bg-primary text-white"><Pin className="w-3 h-3" /> PINNED</span>}
                        {project.featured && <span className="flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded-full glass text-primary"><Star className="w-3 h-3" /> FEATURED</span>}
                    </div>
                    <span className={`absolute top-3 right-3 text-[10px] font-mono px-2 py-1 rounded-full ${STATUS_COLORS[project.status] || "text-muted-foreground bg-secondary"}`}>{project.status}</span>
                </div>
                <div className="p-5" style={{ transform: "translateZ(30px)" }}>
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="font-display text-lg font-semibold">{project.title}</h3>
                        <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:rotate-45 transition-all" />
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{project.summary}</p>
                    <div className="flex flex-wrap gap-1.5 mb-4">
                        {(project.tags || []).slice(0, 4).map((t) => (
                            <span key={t} className="text-[10px] font-mono px-2 py-0.5 rounded bg-secondary text-secondary-foreground">{t}</span>
                        ))}
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-border/40 text-xs font-mono">
                        {project.live ? (
                            <a href={project.live} onClick={(e) => e.stopPropagation()} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300 font-semibold transition-colors bg-emerald-500/10 px-2.5 py-1 rounded-full border border-emerald-500/20 shadow-sm">
                                <ExternalLink className="w-3 h-3" /> Live Demo ↗
                            </a>
                        ) : (
                            <span className="text-muted-foreground text-[11px]">Production Verified</span>
                        )}
                        <a href={project.github || "https://github.com/hariompatel61"} onClick={(e) => e.stopPropagation()} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-foreground hover:text-primary transition-colors font-medium glass px-2.5 py-1 rounded-full">
                            <Github className="w-3.5 h-3.5 text-primary" /> Repo →
                        </a>
                    </div>
                </div>
            </motion.button>
        </Reveal>
    );
}

function Modal({ project, onClose }) {
    if (!project) return null;
    return (
        <AnimatePresence>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
                className="fixed inset-0 z-[95] bg-black/70 backdrop-blur-sm overflow-y-auto p-4 sm:p-8" data-testid="project-modal">
                <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()} className="relative max-w-3xl mx-auto glass rounded-3xl overflow-hidden my-4">
                    <button onClick={onClose} data-testid="close-project-modal" className="absolute top-4 right-4 z-10 w-9 h-9 grid place-items-center rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors"><X className="w-4 h-4" /></button>
                    <img src={project.image} alt={project.title} className="w-full h-56 object-cover" />
                    <div className="p-7">
                        <div className="flex items-center gap-2 mb-3">
                            <span className={`text-[10px] font-mono px-2 py-1 rounded-full ${STATUS_COLORS[project.status] || "bg-secondary"}`}>{project.status}</span>
                            <span className="text-xs text-muted-foreground font-mono">{project.category}</span>
                        </div>
                        <h2 className="font-display text-3xl font-semibold mb-3">{project.title}</h2>
                        <p className="text-muted-foreground leading-relaxed mb-6">{project.summary}</p>

                        {project.metrics && (
                            <div className="grid grid-cols-3 gap-3 mb-6">
                                {Object.entries(project.metrics).map(([k, v]) => (
                                    <div key={k} className="glass rounded-xl p-3 text-center">
                                        <div className="font-display text-xl font-semibold aurora-text">{v}</div>
                                        <div className="text-[10px] text-muted-foreground mt-0.5">{k}</div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="space-y-5 text-sm">
                            <Block icon={ListChecks} title="Features" items={project.features} />
                            <TextBlock icon={Cpu} title="Challenge" text={project.challenges} />
                            <TextBlock icon={Gauge} title="Solution" text={project.solution} />
                            <Block icon={Route} title="Future Roadmap" items={project.roadmap} />
                        </div>

                        <div className="mt-6 mb-2">
                            <div className="flex flex-wrap gap-1.5">
                                {(project.tech || []).map((t) => <span key={t} className="text-xs font-mono px-2.5 py-1 rounded-lg bg-secondary text-secondary-foreground">{t}</span>)}
                            </div>
                        </div>

                        <div className="flex gap-3 mt-6">
                            {project.live && <a href={project.live} target="_blank" rel="noreferrer" data-testid="project-live-link" className="inline-flex items-center gap-2 bg-foreground text-background rounded-full px-5 py-2.5 text-sm font-medium hover:opacity-90 transition-opacity"><ExternalLink className="w-4 h-4" /> Live Demo</a>}
                            <a href={project.github || "https://github.com/hariompatel61"} target="_blank" rel="noreferrer" data-testid="project-github-link" className="inline-flex items-center gap-2 glass rounded-full px-5 py-2.5 text-sm font-medium hover:text-primary transition-colors"><Github className="w-4 h-4" /> Source</a>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

const Block = ({ icon: Icon, title, items }) => (items?.length ? (
    <div>
        <div className="flex items-center gap-2 text-primary mb-2"><Icon className="w-4 h-4" /><span className="font-mono text-xs uppercase tracking-widest">{title}</span></div>
        <div className="flex flex-wrap gap-2">{items.map((x) => <span key={x} className="px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs">{x}</span>)}</div>
    </div>
) : null);

const TextBlock = ({ icon: Icon, title, text }) => (text ? (
    <div>
        <div className="flex items-center gap-2 text-primary mb-2"><Icon className="w-4 h-4" /><span className="font-mono text-xs uppercase tracking-widest">{title}</span></div>
        <p className="text-foreground/80 leading-relaxed">{text}</p>
    </div>
) : null);

const DEFAULT_PROJECTS = [
    {
        id: "ai-hospital",
        title: "AI Hospital Assistant",
        category: "AI",
        featured: true,
        pinned: true,
        status: "Testing",
        summary: "Enterprise medical chatbot featuring Grounded RAG pipelines, vector search, and autonomous SQL Agents over hospital records with Role-Based Access Control (RBAC).",
        image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
        tags: ["RAG", "Vector Search", "FastAPI", "LLMs", "RBAC", "SQL Agent"],
        tech: ["FastAPI", "Python", "MongoDB", "Qdrant", "React", "Docker"],
        features: ["Patient record indexing", "Appointment orchestration", "Hospital billing analysis", "Role-Based Access Control", "Semantic report search"],
        challenges: "Ensuring LLM responses remain strictly grounded in authenticated hospital database schemas without hallucination.",
        solution: "Engineered a dual-layer architecture combining Qdrant vector retrieval with a schema-guarded SQL query engine.",
        roadmap: ["Multi-tenant hospital clustering", "HIPAA compliance logging", "Voice medical charting"],
        live: "https://github.com/hariompatel61/ai-hospital-assistant",
        github: "https://github.com/hariompatel61/ai-hospital-assistant"
    },
    {
        id: "resume-parsing",
        title: "Resume Parsing Platform",
        category: "AI",
        featured: true,
        pinned: true,
        status: "Live",
        summary: "Intelligent recruitment automation engine utilizing Qdrant vector databases and high-dimensional semantic embeddings for zero-latency talent scoring and resume matching.",
        image: "https://images.unsplash.com/photo-1720962158813-29b66b8e23e1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
        tags: ["Vector Search", "ATS", "FastAPI", "Qdrant", "Redis"],
        tech: ["Python", "FastAPI", "Qdrant", "PostgreSQL", "Redis", "Docker", "React"],
        features: ["Semantic candidate matching", "Automated resume extraction", "Real-time ATS scoring", "Redis job caching"],
        challenges: "Parsing unstructured PDF/DOCX resumes into normalized multidimensional schema vectors at scale.",
        solution: "Integrated LLM structural extraction with Qdrant vector similarity indexes and Redis in-memory caching.",
        roadmap: ["Interview scheduling bots", "Automated bias mitigation", "Multi-lingual resume processing"],
        live: "https://github.com/hariompatel61/resume-parsing-platform",
        github: "https://github.com/hariompatel61/resume-parsing-platform"
    },
    {
        id: "culinary-commerce",
        title: "Culinary Commerce",
        category: "Full Stack",
        featured: true,
        pinned: false,
        status: "Live Demo",
        summary: "Complete end-to-end Restaurant Management & POS ecosystem incorporating real-time kitchen displays, multi-branch inventory tracking, and digital customer reservation workflows.",
        image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
        tags: ["POS", "Kitchen Display", "MERN Stack", "Socket.io"],
        tech: ["React", "Node.js", "Express", "MongoDB", "Redux", "Socket.io", "TailwindCSS"],
        features: ["Real-time kitchen order synchronization", "Multi-branch table reservations", "Live inventory tracking", "Point of Sale terminal logic"],
        challenges: "Maintaining state synchronization across front-of-house tablets and kitchen display systems during peak volume.",
        solution: "Implemented event-driven web socket streaming with optimistic Redux client state management.",
        roadmap: ["QR Table self-ordering", "Automated supplier reordering", "Delivery route mapping"],
        live: "https://culinary-commerce-4.emergent.host/",
        github: "https://github.com/hariompatel61/culinary-commerce"
    },
    {
        id: "snapui-extension",
        title: "SnapUI",
        category: "Full Stack",
        featured: false,
        pinned: false,
        status: "Live Extension",
        summary: "High-speed engineering utility designed for developers and QA teams to instantaneously record UI states, annotate user experience bottlenecks, and upload to cloud storage.",
        image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
        tags: ["Chrome Extension", "UI Capture", "Cloud Storage"],
        tech: ["JavaScript", "Chrome API", "React", "Cloud Storage", "TailwindCSS"],
        features: ["Instant browser UI capture", "Interactive visual annotation tools", "Zero-latency cloud uploading", "One-click shareable links"],
        challenges: "Capturing complex high-resolution viewport trees without freezing active DOM scripting.",
        solution: "Engineered background worker thread image processing with chunked asynchronous storage uploads.",
        roadmap: ["Figma design export", "Team workspace collaboration", "Automated bug ticketing integration"],
        live: "https://github.com/hariompatel61/SnapUI",
        github: "https://github.com/hariompatel61/SnapUI"
    },
    {
        id: "image-convert-tools",
        title: "Image Convert Tools",
        category: "Full Stack",
        featured: false,
        pinned: false,
        status: "Live Web App",
        summary: "In-browser privacy-first image conversion, resizing, and compression engine powered by HTML5 Canvas and client-side processing without third-party server data transmission.",
        image: "https://images.unsplash.com/photo-1626785774573-4b799315345d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
        tags: ["Browser Processing", "Zero Latency", "Canvas API", "Privacy"],
        tech: ["React", "TypeScript", "HTML5 Canvas", "WebAssembly", "TailwindCSS"],
        features: ["Zero-server image compression", "Bulk file format conversion", "100% on-device data privacy", "WebAssembly rendering speed"],
        challenges: "Processing gigabyte-scale bulk photo batches entirely in client memory without browser crash.",
        solution: "Utilized tiled HTML5 Canvas memory buffers combined with optimized WebAssembly codecs.",
        roadmap: ["Heif/WebP deep encoding", "AI background remover", "Batch EXIF editor"],
        live: "https://imageconverttools.site/",
        github: "https://github.com/hariompatel61/image-convert-tools"
    },
    {
        id: "qa-toolkit",
        title: "QA Toolkit",
        category: "QA",
        featured: true,
        pinned: false,
        status: "Live Utility",
        summary: "Specialized test automation suite automating Playwright assertion scaffolding, database verification scripts, HTTP API payload inspection, and test bug replication.",
        image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
        tags: ["Test Automation", "Playwright", "Database QA", "Bug Tracking"],
        tech: ["React", "TypeScript", "Playwright", "Selenium", "Python", "Postman", "SQL"],
        features: ["Automated Playwright test scaffolding", "Relational database validation helpers", "HTTP endpoint fuzz testing", "Bug replication report generator"],
        challenges: "Unifying scattered test scripting protocols into a cohesive high-speed enterprise QA suite.",
        solution: "Architected modular tool execution pipelines that output clean, executable test framework assertions.",
        roadmap: ["CI/CD automated pipeline runner", "AI test case generator", "JMeter script exporter"],
        live: "https://qa-toolkit.vercel.app/",
        github: "https://github.com/hariompatel61/qa-toolkit"
    }
];

const LIVE_LINKS_MAP = {
    "Culinary Commerce": "https://culinary-commerce-4.emergent.host/",
    "Image Convert Tools": "https://imageconverttools.site/",
    "QA Toolkit": "https://qa-toolkit.vercel.app/"
};

export default function Projects({ projects }) {
    const [filter, setFilter] = useState("All");
    const [q, setQ] = useState("");
    const [active, setActive] = useState(null);

    const list = useMemo(() => {
        const source = (projects && projects.length > 0) ? projects : DEFAULT_PROJECTS;
        return source.map((p) => ({
            ...p,
            live: LIVE_LINKS_MAP[p.title] || p.live
        }));
    }, [projects]);

    const filtered = useMemo(() => list.filter((p) => {
        const okF = filter === "All" || p.category === filter;
        const okQ = !q || `${p.title} ${p.summary} ${(p.tags || []).join(" ")}`.toLowerCase().includes(q.toLowerCase());
        return okF && okQ;
    }), [list, filter, q]);

    return (
        <section className="max-w-6xl mx-auto px-5 py-24 md:py-32">
            <SectionHeading id="projects" index="02 / Work" subtitle="Selected projects" title="Real production software — from RAG systems to enterprise QA." />

            <div className="flex flex-col sm:flex-row gap-4 justify-between mb-10">
                <div className="flex flex-wrap gap-2">
                    {FILTERS.map((f) => (
                        <button key={f} data-testid={`filter-${f.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => setFilter(f)}
                            className={`px-4 py-2 rounded-full text-sm transition-all ${filter === f ? "bg-foreground text-background" : "glass text-muted-foreground hover:text-foreground"}`}>
                            {f}
                        </button>
                    ))}
                </div>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input data-testid="project-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects…"
                        className="glass rounded-full pl-9 pr-4 py-2 text-sm outline-none focus:ring-1 focus:ring-primary w-full sm:w-56" />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" style={{ transformStyle: "preserve-3d" }}>
                {filtered.map((p, i) => <TiltCard key={p.id} project={p} onOpen={setActive} i={i} />)}
            </div>
            {filtered.length === 0 && <div className="text-center text-muted-foreground py-16">No projects match your search.</div>}

            {active && <Modal project={active} onClose={() => setActive(null)} />}
        </section>
    );
}
